import React, {
  CSSProperties,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import {
  getValidChildrenCount,
  stripUnknownProps,
} from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  flattenStyle,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { defaultRenderNoAutocompleteResults } from '../../helpers/defaultRenderers';
import {
  GetMenuProps,
  DataAttributes,
  FooterProps,
  GetFooterProps,
} from '../../types';

export type MenuProps = StylableComponent<HTMLUListElement> &
  DataAttributes & {
    /** Whether Dropdown menu is open */
    isOpen: boolean;
    /** Toggle loading state */
    isLoading?: boolean;
    /** Whether to show autocomplete-specific features */
    isAutocomplete?: boolean;
    /** Function to get props applied to the <ul> element.  This is returned from the dropdown hook */
    getMenuProps: GetMenuProps;
    /** Function to get props applied to the menu footer.  This is returned from the dropdown hook */
    getFooterProps?: GetFooterProps;
    /** The menu width */
    width?: number;
    /** Custom max-height for the menu container in pixels. Defaults to 300. */
    menuHeight?: number;
    /**
     * Render a component that will wrap the <ul> element, but still be contained within the overlay.
     * Use this to enable virtualization
     */
    renderWrapper?: (
      props: HTMLAttributes<HTMLDivElement>,
    ) => ReactElement | null;
    /** Render a component that will appear at the bottom of the menu */
    renderFooter?: (props: FooterProps) => ReactNode;
    /** Render a component that will appear when isLoading is true */
    renderLoading?: () => ReactNode;
    /** Render a component that will appear at the top of the menu */
    renderHeader?: () => ReactNode;
    /** Render a component that will appear when there are no menu items */
    renderNoAutocompleteResults?: () => ReactNode;
  };

const unusedProps = [
  'menuHeight',
  'selectItem',
  'selectedItem',
  'selectedItems',
  'placement',
  'addSelectedItem', // This doesn't get used but is returned from multiselect hooks
  'removeSelectedItem', // This doesn't get used but is returned from multiselect hooks
  'isGroupHeader',
  'highlightedIndex',
  'groupedItems',
  'getItemKey',
  // These only get used by DropdownMenu and not Menu
  'allowCollapsibleGroups',
  'showGroupItemCount',
  'setExpandedGroupKey',
  'expandedGroupKey',
  'setSelectedGroupKey',
  'selectedGroupKey',
  'showGroupHeaders',
  'allowGroupFiltering',
  'setHighlightedIndex',
  'refToFocusOnClick',
  'showEmptyGroups',
  'dropdownId',
  'allGroupHeadersMenuItems',
  'inputValue',
  'enableSearchHighlight',
];

const scrollStyle: StyleProp = {
  position: 'relative',
  overflowY: 'auto',
};

const LoadingSpinner = () => {
  const { tokens } = useTheme();
  const spinnerTokens = tokens?.component.dropdown?.menu?.loadingSpinner;

  return (
    <div css={spinnerTokens?._}>
      <ProgressRing />
    </div>
  );
};

const defaultRenderLoading = () => <LoadingSpinner />;

const Menu = forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      style = {},
      width,
      isOpen,
      isLoading = false,
      isAutocomplete = false,
      menuHeight,
      getMenuProps,
      getFooterProps,
      renderWrapper,
      renderFooter,
      renderLoading = defaultRenderLoading,
      renderHeader,
      renderNoAutocompleteResults = defaultRenderNoAutocompleteResults,
      children,
      ...props
    },
    ref,
  ) => {
    const { tokens } = useTheme();
    const menuTokens = tokens?.component.dropdown?.menu;
    const containerKey = JSON.stringify(menuTokens?.container?._);
    const listKey = JSON.stringify(menuTokens?.list?._);

    const childrenCount = useMemo(
      () => getValidChildrenCount(children),
      [children],
    );

    const hideHeaderWhenEmptyState = useMemo(
      () =>
        childrenCount <= 0 &&
        isAutocomplete &&
        renderNoAutocompleteResults !== defaultRenderNoAutocompleteResults,
      [childrenCount, isAutocomplete, renderNoAutocompleteResults],
    );

    const showHeader = useMemo(
      () => !!renderHeader && !hideHeaderWhenEmptyState,
      [renderHeader, hideHeaderWhenEmptyState],
    );

    const showHeaderOrFooter = useMemo(
      () => isOpen && (!!renderFooter || showHeader),
      [isOpen, renderFooter, showHeader],
    );

    const wrapperStyle: StyleProp = useMemo(
      () => ({
        ...(!isOpen && {
          height: 0,
        }),
      }),
      [isOpen],
    );

    const resolvedMaxHeight = useMemo(
      () =>
        menuHeight ??
        (isAutocomplete &&
        childrenCount <= 0 &&
        renderNoAutocompleteResults !== defaultRenderNoAutocompleteResults
          ? 1000
          : 300),
      [menuHeight, isAutocomplete, childrenCount, renderNoAutocompleteResults],
    );

    const parentInlineStyle = useMemo(() => {
      const consumerStyle = flattenStyle(style);
      return {
        maxHeight: consumerStyle?.maxHeight ?? resolvedMaxHeight,
      };
    }, [style, resolvedMaxHeight]);

    const parentStyle: StyleProp = useMemo(
      () => [
        menuTokens?.container?._,
        {
          maxWidth: 'calc(100% + 70px)',
          width,
        },
        style,
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [containerKey, style, width],
    );

    const {
      ref: containerRef,
      style: containerStyle,
      ...menuProps
    } = useMemo(() => getMenuProps(), [getMenuProps]);

    const strippedMenuProps = useMemo(
      () => stripUnknownProps(menuProps, unusedProps),
      [menuProps],
    );
    const strippedProvidedProps = useMemo(
      () => stripUnknownProps(props, unusedProps),
      [props],
    );

    const itemsOrMessage = useMemo(
      () => (childrenCount > 0 ? children : renderNoAutocompleteResults()),
      [children, childrenCount, renderNoAutocompleteResults],
    );

    /**
     * Returns the appropriate menu content based on loading and autocomplete states.
     * @returns Loading spinner, autocomplete results, or children
     */
    const getMenuContent = useCallback((): ReactNode => {
      if (isLoading) return renderLoading();
      if (isAutocomplete) return itemsOrMessage;
      return children;
    }, [isLoading, isAutocomplete, itemsOrMessage, children, renderLoading]);

    const renderMenu = useCallback(
      ({
        internalStyle,
        internalInlineStyle,
      }: {
        internalStyle?: StyleProp;
        internalInlineStyle?: CSSProperties;
      } = {}) => (
        <ul
          ref={ref}
          // tabIndex={-1} allows programmatic focus for scrollable region accessibility
          // Focus management is handled via aria-activedescendant on the input/button
          tabIndex={-1}
          // Hide from accessibility tree when closed to avoid "ARIA required children" violation
          // An empty listbox without option children violates ARIA rules
          aria-hidden={!isOpen || undefined}
          css={[menuTokens?.list?._, internalStyle]}
          data-testid="dropdown-menu-items"
          {...strippedMenuProps}
          {...strippedProvidedProps}
          style={internalInlineStyle}
        >
          {isOpen && getMenuContent()}
        </ul>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        getMenuContent,
        isOpen,
        listKey,
        ref,
        strippedMenuProps,
        strippedProvidedProps,
      ],
    );

    const renderContent = useCallback(
      ({
        contentStyle,
        contentInlineStyle,
      }: {
        contentStyle: StyleProp;
        contentInlineStyle?: CSSProperties;
      }) =>
        renderWrapper
          ? renderWrapper({
              style: {
                ...flattenStyle([contentStyle, wrapperStyle]),
                ...contentInlineStyle,
              },
              children: renderMenu(),
            })
          : renderMenu({
              internalStyle: contentStyle,
              internalInlineStyle: contentInlineStyle,
            }),
      [renderMenu, renderWrapper, wrapperStyle],
    );

    const renderHeaderFooterContent = () => (
      <div
        css={[
          {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          parentStyle,
        ]}
        style={parentInlineStyle}
      >
        {showHeader ? renderHeader?.() : null}
        {renderContent({ contentStyle: scrollStyle })}
        {renderFooter?.(getFooterProps?.() || {})}
      </div>
    );

    const renderWrappedContent = () => (
      <div css={parentStyle}>
        {renderContent({
          contentStyle: scrollStyle,
          contentInlineStyle: parentInlineStyle,
        })}
      </div>
    );

    const renderPlainContent = () =>
      renderContent({
        contentStyle: [scrollStyle, parentStyle],
        contentInlineStyle: parentInlineStyle,
      });

    const renderMenuContent = () => {
      if (showHeaderOrFooter) return renderHeaderFooterContent();
      if (renderWrapper) return renderWrappedContent();
      return renderPlainContent();
    };

    return (
      <Overlay.Container
        data-testid={'Dropdown__menu'}
        ref={containerRef as Ref<HTMLDivElement>}
        style={containerStyle as StyleProp}
      >
        {renderMenuContent()}
      </Overlay.Container>
    );
  },
);

Menu.displayName = 'Menu';

Menu.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The menu width */
  width: PropTypes.number,
  /** Custom max-height for the menu container in pixels. Defaults to 300. */
  menuHeight: PropTypes.number,
  /** The content of the Menu */
  children: PropTypes.any,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Function to get props applied to the <ul> element.  This is returned from the dropdown hook */
  getMenuProps: PropTypes.func.isRequired,
  /**
   * Render a component that will wrap the <ul> element, but still be contained within the overlay.
   * Use this is to enable virtualization
   */
  renderWrapper: PropTypes.func,
  /** Render a component that will appear at the bottom of the menu */
  renderFooter: PropTypes.func,
  /** Render a component that will appear when isLoading is true */
  renderLoading: PropTypes.func,
};

export default Menu;
